import { PDFDocument } from 'pdf-lib'

const PAGE_W = 960
const PAGE_H = 540
const PREVIEW_SELECTOR = '[data-proposal-slide-preview]'
const BASE_DECK_URL = '/rocket-presentation/deck-base.pdf'

export type StudioPresentationPdfSession = {
  slideCount: number
  showSlide: (index: number) => Promise<void>
  restore: () => void
}

type LetterCtx = CanvasRenderingContext2D & { letterSpacing: string }

function colorToRgba(value: string): string {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return 'rgba(0, 0, 0, 0)'
  ctx.fillStyle = '#000000'
  ctx.fillStyle = value
  ctx.fillRect(0, 0, 1, 1)
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data
  return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`
}

function isClear(color: string): boolean {
  if (!color || color === 'transparent' || color === 'none') return true
  return colorToRgba(color).endsWith('0.000)')
}

function splitTop(value: string): string[] {
  const out: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i]
    if (ch === '(') depth += 1
    else if (ch === ')') depth = Math.max(0, depth - 1)
    else if (ch === ',' && depth === 0) {
      out.push(value.slice(start, i).trim())
      start = i + 1
    }
  }
  const last = value.slice(start).trim()
  if (last) out.push(last)
  return out
}

function applyStops(grad: CanvasGradient, parts: string[]) {
  const stops = parts.map((part) => {
    const match = part.trim().match(/^(.*?)(?:\s+([\d.]+)%)?\s*$/)
    const raw = (match?.[1] || part).trim()
    const at = match?.[2] ? Number(match[2]) / 100 : null
    const color = raw === 'transparent' ? 'rgba(0, 0, 0, 0)' : colorToRgba(raw)
    return { color, at }
  })
  stops.forEach((stop, index) => {
    const at = stop.at ?? (stops.length === 1 ? 0 : index / (stops.length - 1))
    grad.addColorStop(Math.min(1, Math.max(0, at)), stop.color)
  })
}

function drawBackgroundImage(
  ctx: CanvasRenderingContext2D,
  image: string,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  for (const layer of [...splitTop(image)].reverse()) {
    const kind = /^(linear|radial)-gradient/i.exec(layer)?.[1]?.toLowerCase()
    if (!kind) continue
    const body = layer.replace(/^(linear|radial)-gradient\(/i, '').replace(/\)\s*$/, '')
    const parts = splitTop(body)
    if (parts.length < 2) continue
    if (kind === 'radial') {
      const at = parts[0].match(/([\d.]+)%\s+([\d.]+)%/)
      const cx = x + (at ? Number(at[1]) / 100 : 0.5) * w
      const cy = y + (at ? Number(at[2]) / 100 : 0.5) * h
      const radius = Math.hypot(Math.max(cx - x, x + w - cx), Math.max(cy - y, y + h - cy))
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(radius, 1))
      applyStops(grad, parts.slice(1))
      ctx.fillStyle = grad
      ctx.fillRect(x, y, w, h)
      continue
    }
    const head = parts[0].replace(/\s+in\s+\w+/i, '')
    const named = /to right/i.test(head) ? 90 : /to left/i.test(head) ? 270 : /to top/i.test(head) ? 0 : 180
    const angle = Number(head.match(/([\d.]+)deg/i)?.[1] ?? named)
    const rad = ((angle - 90) * Math.PI) / 180
    const dx = Math.cos(rad)
    const dy = Math.sin(rad)
    const len = Math.abs(w * dx) + Math.abs(h * dy)
    const grad = ctx.createLinearGradient(
      x + w / 2 - (dx * len) / 2,
      y + h / 2 - (dy * len) / 2,
      x + w / 2 + (dx * len) / 2,
      y + h / 2 + (dy * len) / 2,
    )
    applyStops(grad, parts.slice(1))
    ctx.fillStyle = grad
    ctx.fillRect(x, y, w, h)
  }
}

function drawBox(ctx: CanvasRenderingContext2D, style: CSSStyleDeclaration, x: number, y: number, w: number, h: number) {
  const radius = Math.min(Number.parseFloat(style.borderRadius) || 0, w / 2, h / 2)
  const hasImage = Boolean(style.backgroundImage && style.backgroundImage !== 'none')
  if (!isClear(style.backgroundColor) || hasImage) {
    ctx.save()
    ctx.beginPath()
    ctx.roundRect(x, y, w, h, radius)
    ctx.clip()
    if (!isClear(style.backgroundColor)) {
      ctx.fillStyle = colorToRgba(style.backgroundColor)
      ctx.fillRect(x, y, w, h)
    }
    if (hasImage) drawBackgroundImage(ctx, style.backgroundImage, x, y, w, h)
    ctx.restore()
  }

  const widths = ['Top', 'Right', 'Bottom', 'Left'].map((side) => Number.parseFloat(style.getPropertyValue(`border-${side.toLowerCase()}-width`)) || 0)
  const [top, right, bottom, left] = widths
  if (top + right + bottom + left <= 0) return
  ctx.save()
  if (top === right && right === bottom && bottom === left) {
    ctx.strokeStyle = colorToRgba(style.borderTopColor)
    ctx.lineWidth = top
    ctx.beginPath()
    ctx.roundRect(x + top / 2, y + top / 2, Math.max(0, w - top), Math.max(0, h - top), Math.max(0, radius - top / 2))
    ctx.stroke()
  } else if (bottom > 0) {
    ctx.strokeStyle = colorToRgba(style.borderBottomColor)
    ctx.lineWidth = bottom
    ctx.beginPath()
    ctx.moveTo(x, y + h - bottom / 2)
    ctx.lineTo(x + w, y + h - bottom / 2)
    ctx.stroke()
  }
  ctx.restore()
}

function wrapToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (line && ctx.measureText(next).width > maxWidth + 1) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  }
  if (line) lines.push(line)
  return lines.length ? lines : [text]
}

function drawText(ctx: CanvasRenderingContext2D, el: HTMLElement, style: CSSStyleDeclaration, origin: DOMRect) {
  const nodes = [...el.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
  if (!nodes.length) return
  const fontSize = Number.parseFloat(style.fontSize) || 12
  ctx.save()
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  ctx.fillStyle = colorToRgba(style.color)
  ;(ctx as LetterCtx).letterSpacing = style.letterSpacing || '0px'
  ctx.textBaseline = 'top'
  for (const node of nodes) {
    const raw = (node.textContent || '').replace(/\s+/g, ' ').trim()
    const text = style.textTransform === 'uppercase'
      ? raw.toLocaleUpperCase('pt-BR')
      : style.textTransform === 'lowercase'
        ? raw.toLocaleLowerCase('pt-BR')
        : raw
    const range = document.createRange()
    range.selectNodeContents(node)
    const rects = [...range.getClientRects()].filter((rect) => rect.width > 0.5 && rect.height > 0.5)
    const lines = style.whiteSpace === 'nowrap' || rects.length < 2
      ? [text]
      : wrapToWidth(ctx, text, rects[0]?.width || el.getBoundingClientRect().width)
    const boxes = rects.length ? rects : [el.getBoundingClientRect()]
    lines.forEach((line, index) => {
      const rect = boxes[Math.min(index, boxes.length - 1)]
      const top = rect.top - origin.top + Math.max(0, (rect.height - fontSize) / 2)
      ctx.fillText(line, rect.left - origin.left, top)
    })
  }
  ctx.restore()
}

/** Desenha o preview nas posições que o navegador já mediu. Não recalcula o layout. */
function paintPreview(source: HTMLElement): HTMLCanvasElement {
  const origin = source.getBoundingClientRect()
  const width = Math.max(1, Math.round(origin.width))
  const height = Math.max(1, Math.round(origin.height))
  const scale = Math.min(3, Math.max(2, 1920 / width))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Falha ao montar o slide de valor.')
  ctx.scale(scale, scale)
  ctx.fillStyle = '#070712'
  ctx.fillRect(0, 0, width, height)

  for (const el of [source, ...source.querySelectorAll<HTMLElement>('*')]) {
    const style = getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) continue
    const box = el.getBoundingClientRect()
    if (box.width < 0.5 || box.height < 0.5) continue
    const x = box.left - origin.left
    const y = box.top - origin.top
    ctx.save()
    ctx.globalAlpha = Number(style.opacity) || 1
    drawBox(ctx, style, x, y, box.width, box.height)
    drawText(ctx, el, style, origin)
    ctx.restore()
  }
  return canvas
}

async function captureInvestmentSlide(): Promise<Uint8Array> {
  const source = document.querySelector(PREVIEW_SELECTOR)
  if (!(source instanceof HTMLElement)) {
    throw new Error('Abra o visual da apresentação para exportar.')
  }
  const box = source.getBoundingClientRect()
  if (box.width < 40 || box.height < 40) {
    throw new Error('Abra o visual da apresentação para exportar.')
  }
  await document.fonts.ready
  const canvas = paintPreview(source)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('Falha ao montar o slide de valor.')
  return new Uint8Array(await blob.arrayBuffer())
}

async function appendValueSlide(pdf: PDFDocument, pngBytes: Uint8Array) {
  const page = pdf.addPage([PAGE_W, PAGE_H])
  const image = await pdf.embedPng(pngBytes)
  const fitted = image.width / image.height > PAGE_W / PAGE_H
    ? { width: PAGE_W, height: (PAGE_W / image.width) * image.height }
    : { height: PAGE_H, width: (PAGE_H / image.height) * image.width }
  page.drawImage(image, {
    x: (PAGE_W - fitted.width) / 2,
    y: (PAGE_H - fitted.height) / 2,
    width: fitted.width,
    height: fitted.height,
  })
}

/** 15 páginas originais do PowerPoint e, no fim, o slide de valor do estúdio. */
export async function downloadStudioPresentationPdf(
  empresaNome: string,
  session: StudioPresentationPdfSession,
): Promise<void> {
  if (session.slideCount < 1) {
    throw new Error('Adicione pelo menos um item à proposta')
  }

  const response = await fetch(BASE_DECK_URL)
  if (!response.ok) throw new Error('Não foi possível carregar a apresentação base.')
  const pdf = await PDFDocument.load(await response.arrayBuffer())

  for (let index = 0; index < session.slideCount; index += 1) {
    await session.showSlide(index)
    await appendValueSlide(pdf, await captureInvestmentSlide())
  }

  const safeName = empresaNome.replace(/[^\w\-]+/g, '_').replace(/_+/g, '_') || 'Cliente'
  const bytes = await pdf.save()
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `Apresentacao_${safeName}.pdf`
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
